import React, { useEffect, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import { getClients } from "../../../api";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";

const FILTER_DEBOUNCE_MS = 400;

// O vínculo do Calculator API é o id do CLIENTE — o mesmo `cliente_id` da URL
// /clientes/:cliente_id e o `client_id` que o backend usa nas rotas de cliente.
// NUNCA o id de usuário: um cliente tem vários usuários, e mandar um user_id
// vincularia a um cliente errado (ou a nenhum) sem erro nenhum, porque o
// calculator-api não valida o número contra a base da Jusfy.
//
// Esta linha vem de GET /admin/clients, onde `id` já é o id do cliente. Fica isolada
// aqui para não haver `row.<campo>` solto em nenhum outro ponto da tela.
const jusfyClientIdOf = jusfyClientRow => Number(jusfyClientRow.id);

// A listagem de clientes da Jusfy, aqui só para vincular: o admin acha o cliente
// pelo nome e clica, em vez de copiar o id numérico da URL de /clientes/:id e
// digitar à mão — um dígito trocado vincularia ao cliente errado em silêncio,
// porque um id vizinho também é válido.
//
// Paginada no servidor, como em /clientes: esta rota pagina (a de clientes do
// calculator-api é que não).
export default function JusfyClientsTable({ linkByJusfyClientId, onCreateLink }) {
  const [jusfyClients, setJusfyClients] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [nameFilter, setNameFilter] = useState("");
  const [pagination, setPagination] = useState({ page: 1, perPage: 10 });

  const debouncedName = useDebouncedValue(nameFilter, FILTER_DEBOUNCE_MS);

  // digitar rápido põe várias buscas em voo; a que for descartada não escreve
  const requestIdRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);

    // getClients já avisa por toast e devolve lista vazia quando falha
    getClients({
      page: pagination.page,
      perPage: pagination.perPage,
      name: debouncedName,
    })
      .then(response => {
        if (requestId !== requestIdRef.current) return;
        setJusfyClients(response.data.data);
        setTotalRows(response.data.total);
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
  }, [pagination, debouncedName]);

  const filterByName = value => {
    setNameFilter(value);
    setPagination(current => ({ ...current, page: 1 }));
  };

  const linkOf = jusfyClientRow =>
    linkByJusfyClientId.get(String(jusfyClientIdOf(jusfyClientRow)));

  const columns = [
    {
      name: "ID do cliente",
      selector: jusfyClientIdOf,
      width: "130px",
    },
    { name: "Nome", selector: row => row.name, grow: 2 },
    { name: "E-mail", selector: row => row.email, grow: 2 },
    {
      name: "Cliente no Calculator API",
      cell: row => linkOf(row)?.name ?? "—",
      grow: 2,
    },
    {
      name: "Ação",
      cell: row =>
        linkOf(row) ? (
          <span className="text-muted">Vinculado</span>
        ) : (
          <button
            type="button"
            className="btn btn-sm btn-primary"
            // a tela de cima recebe o id do cliente já extraído e convertido,
            // nunca a linha crua — não há campo para escolher errado
            onClick={() =>
              onCreateLink({
                jusfyClientId: jusfyClientIdOf(row),
                name: row.name,
              })
            }
          >
            Criar vínculo
          </button>
        ),
      ignoreRowClick: true,
      allowOverflow: true,
      width: "160px",
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={jusfyClients}
      progressPending={loading}
      pagination
      paginationServer
      paginationTotalRows={totalRows}
      onChangePage={page => setPagination(current => ({ ...current, page }))}
      onChangeRowsPerPage={(perPage, page) => setPagination({ page, perPage })}
      noDataComponent="Nenhum cliente da Jusfy encontrado."
      subHeader
      subHeaderComponent={
        <input
          type="text"
          className="form-control"
          style={{ width: 240 }}
          placeholder="Buscar por nome"
          value={nameFilter}
          onChange={event => filterByName(event.target.value)}
        />
      }
    />
  );
}
